/**
 * Social Trust Manager - Core Social Authorization, Relationship & Consent Engine
 *
 * Implements:
 * - Deterministic relationship transitions (Unknown, Requested, Accepted, Blocked, Muted)
 * - Capability-based invitation verification and revocation
 * - Rate limiting against automated mass outreach
 * - Cross-app authorization queries:
 *   - canContact(actorId, targetId)
 *   - canInviteToGroup(actorId, targetId, groupId, tier)
 *   - canAccessLogistics(viewerId, hostId, participantStatus)
 */

import type {
  SocialRelationshipState,
  SocialContactRecord,
  InvitationCapability,
  GroupGovernanceTier,
  GOVERNANCE_TIER_LIMITS,
} from '@yada/types';

export interface StorageAdapter {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
}

const CONTACTS_KEY = 'yada:social:contacts';
const INVITATIONS_KEY = 'yada:social:invitations';
const RATE_LIMIT_KEY = 'yada:social:ratelimits';

interface RateLimitBucket {
  count: number;
  resetAt: number;
}

export class SocialTrustManager {
  private storage: StorageAdapter;
  private contactsCache: Map<string, SocialContactRecord> = new Map();
  private invitationsCache: Map<string, InvitationCapability> = new Map();
  private rateLimits: Map<string, RateLimitBucket> = new Map();
  private initialized = false;

  constructor(storage: StorageAdapter) {
    this.storage = storage;
  }

  public async init(): Promise<void> {
    if (this.initialized) return;
    const storedContacts = await this.storage.get<SocialContactRecord[]>(CONTACTS_KEY);
    if (storedContacts && Array.isArray(storedContacts)) {
      for (const c of storedContacts) {
        this.contactsCache.set(c.targetProfileId, c);
      }
    }

    const storedInvitations = await this.storage.get<InvitationCapability[]>(INVITATIONS_KEY);
    if (storedInvitations && Array.isArray(storedInvitations)) {
      for (const inv of storedInvitations) {
        this.invitationsCache.set(inv.id, inv);
      }
    }
    this.initialized = true;
  }

  // ── Relationship Management ────────────────────────────────────────────────

  public getRelationshipState(targetProfileId: string): SocialRelationshipState {
    const record = this.contactsCache.get(targetProfileId);
    return record?.state ?? 'unknown';
  }

  public async requestContact(myProfileId: string, targetProfileId: string): Promise<{ success: boolean; error?: string }> {
    await this.init();
    if (targetProfileId === myProfileId) {
      return { success: false, error: 'Cannot request contact with oneself' };
    }

    const current = this.getRelationshipState(targetProfileId);
    if (current === 'blocked') {
      // Zero-knowledge silent reject
      return { success: false, error: 'Contact request rejected' };
    }
    if (current === 'accepted') {
      return { success: true };
    }
    if (current === 'requested_outbound') {
      return { success: false, error: 'A request is already pending' };
    }

    // Rate-limiting check: max 10 contact requests per 10 minutes
    if (!this.checkRateLimit(`contact_req:${myProfileId}`, 10, 600_000)) {
      return { success: false, error: 'Rate limit exceeded for contact requests' };
    }

    const now = new Date().toISOString();
    const record: SocialContactRecord = {
      targetProfileId,
      state: 'requested_outbound',
      createdAt: now,
      updatedAt: now,
    };
    this.contactsCache.set(targetProfileId, record);
    await this.persistContacts();
    return { success: true };
  }

  public async acceptContact(targetProfileId: string): Promise<void> {
    await this.init();
    const now = new Date().toISOString();
    const record: SocialContactRecord = this.contactsCache.get(targetProfileId) ?? {
      targetProfileId,
      state: 'accepted',
      createdAt: now,
      updatedAt: now,
    };
    record.state = 'accepted';
    record.updatedAt = now;
    this.contactsCache.set(targetProfileId, record);
    await this.persistContacts();
  }

  public async blockProfile(targetProfileId: string): Promise<void> {
    await this.init();
    const now = new Date().toISOString();
    const record: SocialContactRecord = {
      targetProfileId,
      state: 'blocked',
      createdAt: now,
      updatedAt: now,
    };
    this.contactsCache.set(targetProfileId, record);
    await this.persistContacts();
  }

  public async unblockProfile(targetProfileId: string): Promise<void> {
    await this.init();
    const record = this.contactsCache.get(targetProfileId);
    if (record && record.state === 'blocked') {
      this.contactsCache.delete(targetProfileId);
      await this.persistContacts();
    }
  }

  public async muteProfile(targetProfileId: string): Promise<void> {
    await this.init();
    const now = new Date().toISOString();
    const record: SocialContactRecord = this.contactsCache.get(targetProfileId) ?? {
      targetProfileId,
      state: 'muted',
      createdAt: now,
      updatedAt: now,
    };
    record.state = 'muted';
    record.updatedAt = now;
    this.contactsCache.set(targetProfileId, record);
    await this.persistContacts();
  }

  public isBlocked(targetProfileId: string): boolean {
    return this.getRelationshipState(targetProfileId) === 'blocked';
  }

  public isMuted(targetProfileId: string): boolean {
    return this.getRelationshipState(targetProfileId) === 'muted';
  }

  public getBlockedProfileIds(): string[] {
    const blocked: string[] = [];
    for (const [id, record] of this.contactsCache.entries()) {
      if (record.state === 'blocked') blocked.push(id);
    }
    return blocked;
  }

  // ── Capability-Based Invitations ────────────────────────────────────────────

  public async createInvitationCapability(params: {
    resourceType: InvitationCapability['resourceType'];
    resourceId: string;
    issuerProfileId: string;
    targetProfileId?: string;
    maxUses?: number;
    durationMs?: number;
  }): Promise<InvitationCapability> {
    await this.init();
    // Rate limit invitation generation: max 20 per 10 minutes
    if (!this.checkRateLimit(`inv_create:${params.issuerProfileId}`, 20, 600_000)) {
      throw new Error('Rate limit exceeded for invitation creation');
    }

    const now = new Date();
    const id = `inv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const capability: InvitationCapability = {
      id,
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      issuerProfileId: params.issuerProfileId,
      targetProfileId: params.targetProfileId,
      maxUses: params.maxUses ?? 1,
      usedCount: 0,
      createdAt: now.toISOString(),
      expiresAt: params.durationMs ? new Date(now.getTime() + params.durationMs).toISOString() : undefined,
    };

    this.invitationsCache.set(id, capability);
    await this.persistInvitations();
    return capability;
  }

  public async verifyAndConsumeInvitation(
    invitationId: string,
    consumerProfileId: string
  ): Promise<{ valid: boolean; error?: string; capability?: InvitationCapability }> {
    await this.init();
    const capability = this.invitationsCache.get(invitationId);
    if (!capability) {
      return { valid: false, error: 'Invitation not found' };
    }

    if (capability.revokedAt) {
      return { valid: false, error: 'Invitation has been revoked' };
    }

    if (capability.expiresAt && new Date(capability.expiresAt) < new Date()) {
      return { valid: false, error: 'Invitation has expired' };
    }

    if (capability.targetProfileId && capability.targetProfileId !== consumerProfileId) {
      return { valid: false, error: 'Invitation is not intended for this profile' };
    }

    if (capability.usedCount >= capability.maxUses) {
      return { valid: false, error: 'Invitation usage limit exceeded' };
    }

    // Check if consumer has blocked issuer or vice-versa
    if (this.isBlocked(capability.issuerProfileId)) {
      return { valid: false, error: 'Interaction disallowed due to block state' };
    }

    capability.usedCount += 1;
    await this.persistInvitations();
    return { valid: true, capability };
  }

  public async revokeInvitation(invitationId: string, revokedByProfileId: string): Promise<boolean> {
    await this.init();
    const capability = this.invitationsCache.get(invitationId);
    if (!capability || capability.issuerProfileId !== revokedByProfileId) {
      return false;
    }
    capability.revokedAt = new Date().toISOString();
    await this.persistInvitations();
    return true;
  }

  // ── Authorization & Gate Checks ────────────────────────────────────────────

  /**
   * Universal check: Can actor A initiate communication with target B?
   * Requires either:
   * 1. Direct relationship is 'accepted'
   * 2. A valid invitation capability is provided
   */
  public canCommunicate(
    actorId: string,
    targetId: string,
    invitation?: InvitationCapability
  ): boolean {
    if (actorId === targetId) return true;
    if (this.isBlocked(targetId) || this.isBlocked(actorId)) return false;

    const state = this.getRelationshipState(targetId) !== 'unknown' 
      ? this.getRelationshipState(targetId) 
      : this.getRelationshipState(actorId);

    if (state === 'accepted') return true;

    if (invitation && !invitation.revokedAt && invitation.usedCount < invitation.maxUses) {
      if (invitation.targetProfileId && invitation.targetProfileId !== actorId) {
        return false;
      }
      return true;
    }

    return false;
  }

  /**
   * Logistics access level: Determines what physical details a viewer can see.
   */
  public getLogisticsAccessLevel(
    viewerProfileId: string,
    hostProfileId: string,
    participationStatus?: string
  ): 'host' | 'attendee' | 'public' {
    if (viewerProfileId === hostProfileId) return 'host';
    if (participationStatus === 'confirmed' || participationStatus === 'accepted') return 'attendee';
    return 'public';
  }

  // ── Rate Limiting Internal Helper ──────────────────────────────────────────

  private checkRateLimit(key: string, maxLimit: number, windowMs: number): boolean {
    const now = Date.now();
    const bucket = this.rateLimits.get(key);
    if (!bucket || bucket.resetAt <= now) {
      this.rateLimits.set(key, { count: 1, resetAt: now + windowMs });
      return true;
    }

    if (bucket.count >= maxLimit) {
      return false;
    }

    bucket.count += 1;
    return true;
  }

  private async persistContacts(): Promise<void> {
    const records = Array.from(this.contactsCache.values());
    await this.storage.set(CONTACTS_KEY, records);
  }

  private async persistInvitations(): Promise<void> {
    const records = Array.from(this.invitationsCache.values());
    await this.storage.set(INVITATIONS_KEY, records);
  }
}
